import { useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Award, Flame, Pencil, BarChart3, CalendarDays } from "lucide-react";
import { birthdayBadgeStatus, anniversaryBadgeStatus } from "@/lib/celebrations";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { EditAvatarDialog } from "@/components/EditAvatarDialog";
import { PresenceChips } from "@/components/PresenceChips";
import { Select, SelectTrigger, SelectContent, SelectItem } from "@/components/ui/select";

// Iter 133 — the card view moved out of the Who's-in slide-out (now list-only)
// and became the board on the full In/Out app, so the markup lives here and is
// shared. `large` renders the roomier app-page variant; the edit / note /
// upcoming-PTO affordances only appear when the caller passes their handlers.
export const STATUS_META = {
  in:    { label: "In",       bar: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
  break: { label: "On Break", bar: "bg-sky-500",     chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300",             dot: "bg-sky-500" },
  lunch: { label: "Lunch",    bar: "bg-amber-500",   chip: "bg-amber-500/10 text-amber-700 dark:text-amber-300",       dot: "bg-amber-500" },
  out:   { label: "Out",      bar: "bg-slate-500",   chip: "bg-slate-500/10 text-slate-700 dark:text-slate-300",       dot: "bg-slate-500" },
  pto:   { label: "Vacation", bar: "bg-violet-500",  chip: "bg-violet-500/10 text-violet-700 dark:text-violet-300",    dot: "bg-violet-500" },
};
export const ORDER = ["in", "break", "lunch", "out", "pto"];

export const roleLabel = (role) => ({
  super_admin: "Super Admin",
  admin: "Admin",
  techops: "TechOps",
  partner_admin: "Partner Admin",
  partner: "Partner",
  customer: "Customer",
}[role] || (role ? role.replace(/_/g, " ") : ""));

export const fmtPtoRange = (a, b) => {
  if (!a && !b) return "";
  const f = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" }) : "");
  return a && b ? `${f(a)} – ${f(b)}` : f(a || b);
};

export const fmtRelative = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "";
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
};

const teamsTone = (teams) =>
  teams === "Available" ? "bg-emerald-500 text-white"
  : teams === "Busy" || teams === "Do not disturb" || teams === "In a meeting" ? "bg-rose-500 text-white"
  : teams === "Away" ? "bg-amber-400 text-amber-900"
  : "bg-slate-400 text-white";

// Iter 136 — card outline mirrors live Teams presence: bright green when in,
// bright orange when away, bright red when offline / busy. People we can't read
// at all (no Teams mapping) keep the neutral border rather than a false signal.
const presenceRing = (teams) => {
  if (teams === "Available") return "border-[3px] border-emerald-500";
  if (teams === "Away" || teams === "Be right back") return "border-[3px] border-orange-500";
  if (teams) return "border-[3px] border-red-500";
  return "border border-border";
};

export function WhoIsInCard({
  row, me, appId, prefix = "whos-in", large = false,
  onAvatarUpdated, onHistory, onStatusChange, onNote, onUpcoming,
}) {
  const { user } = useAuth();
  const cel = user?.celebrations;
  const [editOpen, setEditOpen] = useState(false);
  const meta = STATUS_META[row.status] || STATUS_META.out;
  const on = cel?.enabled !== false;
  const b = on ? birthdayBadgeStatus(row.birthday, cel?.days_ahead) : null;
  const a = on && cel?.include_anniversaries !== false
    ? anniversaryBadgeStatus(row.work_anniversary, cel?.days_ahead) : null;
  const updated = fmtRelative(row.status_updated_at || row.updated_at);
  const isSelf = me && me.user_id === row.user_id;
  const teams = (row.teams_status || "").trim();
  const upcoming = row.upcoming_pto || [];

  return (
    <div data-testid={`${prefix}-card-${row.user_id}`} data-presence={teams || "unknown"}
      className={`group relative flex flex-col items-center rounded-xl ${presenceRing(teams)} bg-card text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
        large ? "gap-2 p-4" : "gap-1.5 p-2.5"}`}>
      {/* Celebrations sit top-left so they never collide with the status pill */}
      <div className="absolute left-1.5 top-1.5 flex items-center gap-1">
        {b?.flash && <span data-testid={`${prefix}-birthday-${row.user_id}`} title={b.label} className="celebrate-flash"><Cake className="celebrate-icon h-3.5 w-3.5 text-amber-500" /></span>}
        {a?.flash && <span data-testid={`${prefix}-anniv-${row.user_id}`} title={a.label} className="celebrate-flash"><Award className="celebrate-icon h-3.5 w-3.5 text-violet-500" /></span>}
      </div>
      {onStatusChange && (
        <Select value={row.status} onValueChange={(v) => onStatusChange(row, v)}>
          <SelectTrigger data-testid={`${prefix}-status-select-${row.user_id}`}
            className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md border-0 bg-transparent p-0 opacity-0 shadow-none transition-opacity hover:bg-muted focus:opacity-100 group-hover:opacity-100 [&>svg]:hidden">
            <Pencil className="h-3 w-3 text-muted-foreground" />
          </SelectTrigger>
          <SelectContent>
            {ORDER.map((s) => (
              <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${STATUS_META[s].dot}`} />{STATUS_META[s].label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="relative">
        <Avatar name={row.name} src={row.avatar_url} size={large ? "xl" : "md"} fallback="mystery"
          testid={`${prefix}-avatar-${row.user_id}`} />
        {teams && (
          <span data-testid={`${prefix}-teams-${row.user_id}`} title={`Microsoft Teams: ${teams}`}
            className={`absolute -top-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none shadow ring-1 ring-white ${teamsTone(teams)}`}>
            {teams}
          </span>
        )}
        {row.on_call && (
          <span data-testid={`${prefix}-oncall-${row.user_id}`} title="On call via incident.io"
            className="absolute -bottom-1 -right-1 inline-flex items-center rounded-full bg-orange-500 px-1 py-0.5 text-white shadow ring-1 ring-white">
            <Flame className="h-2.5 w-2.5" fill="currentColor" />
          </span>
        )}
        {isSelf && (
          <button type="button" onClick={() => setEditOpen(true)} title="Change your avatar"
            data-testid={`${prefix}-edit-avatar-${row.user_id}`}
            className="absolute -bottom-1 -left-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow hover:text-primary">
            <Pencil className="h-2.5 w-2.5" />
          </button>
        )}
      </div>

      <div className="w-full min-w-0">
        <div className={`truncate font-semibold text-foreground ${large ? "text-base" : "text-xs"}`} title={row.name}>
          {row.name}
          {isSelf && <span className="ml-1 rounded bg-primary/10 px-1 align-middle text-[9px] font-medium text-primary">You</span>}
        </div>
        <div data-testid={`${prefix}-teamlist-${row.user_id}`} title={(row.teams || []).join(", ")}
          className={`text-muted-foreground ${large ? "text-xs" : "text-[10px]"}`}>
          {/* Iter 142/143 — every team, comma delimited, each linking to its page. */}
          {(row.teams || []).length
            ? row.teams.map((t, i) => (
                <span key={t}>
                  {i > 0 && ", "}
                  <Link to={`/teams/${encodeURIComponent(t)}`} data-testid={`${prefix}-teamlink-${row.user_id}-${i}`}
                    className="hover:text-primary hover:underline">{t}</Link>
                </span>
              ))
            : (row.team || roleLabel(row.role) || "—")}
          {row.role === "techops" && row.qualification && (
            <span data-testid={`${prefix}-qual-${row.user_id}`}
              className="ml-1 rounded bg-emerald-500/10 px-1 py-0.5 text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-300">{row.qualification}</span>
          )}
        </div>
      </div>

      <div className="w-full"><PresenceChips row={row} appId={appId} prefix={`${prefix}-card`} /></div>

      <div data-testid={`${prefix}-status-${row.user_id}`}
        className={`w-full rounded px-2 text-center font-semibold text-white ${meta.bar} ${large ? "py-1.5 text-sm" : "py-0.5 text-[10px]"}`}>
        {meta.label}
        {row.status === "pto" && fmtPtoRange(row.pto_start, row.pto_end) && (
          <span className="ml-1 font-normal opacity-90">· {fmtPtoRange(row.pto_start, row.pto_end)}</span>
        )}
      </div>
      {updated && (
        <div className="text-[9px] text-muted-foreground" data-testid={`${prefix}-updated-${row.user_id}`}>{updated}</div>
      )}

      {onNote && (
        row.note || (row.updated_by_name && row.updated_by_id !== row.user_id) ? (
          <div className="flex w-full items-center gap-1 text-[11px] text-muted-foreground">
            {row.note && (
              <span className="truncate text-foreground/80"
                title={row.note_by ? `${row.note} — added by ${row.note_by}` : row.note}>
                “{row.note}”{row.note_by ? ` — ${row.note_by.split(/\s+/)[0]}` : ""}
              </span>
            )}
            {row.updated_by_name && row.updated_by_id !== row.user_id && (
              <span className="ml-auto shrink-0 text-amber-600 dark:text-amber-400">
                via {row.updated_by_id === "msgraph" ? "Teams" : row.updated_by_name.split(/\s+/)[0]}
              </span>
            )}
            <button title="Edit note" data-testid={`${prefix}-note-edit-${row.user_id}`}
              onClick={() => onNote(row)} className="shrink-0 text-muted-foreground hover:text-foreground">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button data-testid={`${prefix}-note-edit-${row.user_id}`} onClick={() => onNote(row)}
            className="w-full text-center text-[11px] text-muted-foreground/60 hover:text-muted-foreground">+ add note</button>
        )
      )}

      <div className="mt-auto flex w-full items-center justify-center gap-2 pt-1">
        <button type="button" data-testid={`${prefix}-history-${row.user_id}`}
          title={`Presence history for ${row.name}`}
          onClick={() => onHistory?.({ user_id: row.user_id, name: row.name })}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-primary">
          <BarChart3 className="h-3 w-3" /> Week
        </button>
      </div>

      {onUpcoming && (
        <div className="flex w-full items-center gap-1 border-t border-border pt-1.5">
          <CalendarDays className="h-3 w-3 shrink-0 text-muted-foreground" />
          {upcoming.length ? (
            <span className="truncate text-[10px] text-foreground/80" data-testid={`${prefix}-upcoming-${row.user_id}`}>
              {fmtPtoRange(upcoming[0].start, upcoming[0].end)}
              {upcoming[0].note ? ` · ${upcoming[0].note}` : ""}
              {upcoming.length > 1 ? ` +${upcoming.length - 1}` : ""}
            </span>
          ) : (
            <span className="text-[10px] text-muted-foreground/50">No upcoming PTO</span>
          )}
          <button title="Edit upcoming PTO" data-testid={`${prefix}-upcoming-edit-${row.user_id}`}
            onClick={() => onUpcoming(row)} className="ml-auto shrink-0 text-muted-foreground hover:text-foreground">
            <Pencil className="h-3 w-3" />
          </button>
        </div>
      )}

      {isSelf && (
        <EditAvatarDialog open={editOpen} onOpenChange={setEditOpen} user={row}
          onSaved={() => onAvatarUpdated?.()} />
      )}
    </div>
  );
}
